<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <div class="header-topic">
            <h4>
              <i class="fas fa-file-invoice-dollar"></i>
              {{ entity.title }}
            </h4>
          </div>

          <b-card no-body>
            <b-tabs
              card
              v-model="activeTabIndex"
            >
              <b-tab
                v-for="tab in tabs"
                :key="tab.status"
              >
                <template #title>
                  {{ tab.label }}{{ tab.showCount ? ` (${tab.count})` : "" }}
                  <span
                    v-if="tab.showRedDot && tab.count > 0"
                    class="tab-red-dot"
                  ></span>
                </template>
              </b-tab>
            </b-tabs>

            <div class="p-3">
              <div class="d-flex mb-3">
                <b-form-input
                  type="search"
                  autofocus
                  v-model="filter"
                  :placeholder="entity.searchPlaceholder"
                  data-testid="cost-gl-search"
                ></b-form-input>
                <b-button
                  v-if="canEditMaster"
                  variant="primary"
                  class="ml-3 text-nowrap"
                  data-testid="master-add"
                  @click="openMasterModal()"
                >
                  <i class="fas fa-plus"></i> เพิ่มรายการ
                </b-button>
              </div>

              <!-- rows arrive a page (COST_GL_PAGE_SIZE) at a time as the user scrolls down -->
              <div
                ref="scrollBox"
                class="cost-gl-scroll"
                data-testid="cost-gl-scroll"
                @scroll.passive="onScroll"
              >
              <b-table
                hover
                small
                sort-icon-left
                head-variant="light"
                :responsive="false"
                :items="rows"
                :fields="fields"
                :busy="isLoading"
                show-empty
                empty-text="ไม่พบรายการ"
                data-testid="cost-gl-table"
              >
                <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
                <template v-slot:cell(status)="row">
                  <MasterStatusBadge :status="row.item.status" />
                </template>
                <template v-slot:cell(actions)="row">
                  <template v-if="canAssignGl">
                    <b-button
                      v-if="activeStatus === MASTER_STATUS.PENDING"
                      variant="primary"
                      size="sm"
                      data-testid="cost-gl-add"
                      @click="openForGl(row.item)"
                    >
                      เพิ่ม Cost G/L
                    </b-button>
                    <b-button
                      v-else-if="!canEditMaster"
                      size="sm"
                      variant="outline-secondary"
                      title="แก้ไข Cost G/L"
                      data-testid="cost-gl-edit"
                      @click="openForGl(row.item)"
                    >
                      <i class="fas fa-edit"></i>
                    </b-button>
                  </template>
                  <template v-if="canEditMaster">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      :class="{ 'ml-2': canAssignGl }"
                      title="แก้ไขรายการ"
                      data-testid="master-edit"
                      @click="openMasterModal(row.item)"
                    >
                      <i class="fas fa-edit"></i>
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      class="ml-2"
                      title="ลบรายการ"
                      data-testid="master-delete"
                      @click="deleteMaster(row.item)"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </b-button>
                  </template>
                </template>
              </b-table>
              </div>
              <div
                class="text-center text-muted small py-2"
                data-testid="cost-gl-footer"
              >
                <template v-if="isLoadingMore">กำลังโหลดเพิ่ม...</template>
                <template v-else-if="total > 0"
                  >แสดง {{ rows.length }} จาก {{ total }} รายการ</template
                >
              </div>
            </div>
          </b-card>
        </div>
      </div>

      <TxItem
        v-if="entity.key === 'treat'"
        ref="MasterModal"
        clinic-mode
        @saved="reload"
      ></TxItem>
      <DrugsItem
        v-if="entity.key === 'item'"
        ref="MasterModal"
        clinic-mode
        @saved="reload"
        @loading="isLoading = $event"
      ></DrugsItem>
      <LabProductItem
        v-if="entity.key === 'lab'"
        ref="MasterModal"
        clinic-mode
        @saved="reload"
        @loading="isLoading = $event"
      ></LabProductItem>
      <Dialog ref="Dialog"></Dialog>
      <Loading v-if="isLoading && !rows.length"></Loading>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog.vue";
import MasterStatusBadge from "@/components/badge/MasterStatusBadge.vue";
import TxItem from "@/components/modal/TxItem.vue";
import DrugsItem from "@/components/modal/DrugsItem.vue";
import LabProductItem from "@/components/modal/LabProductItem.vue";
import debounce from "lodash/debounce";
import tabQueryMixin from "@/mixins/tabQueryMixin";
import {
  COST_GL_ENTITIES,
  COST_GL_PAGE_SIZE,
  COST_GL_TABS,
  MASTER_STATUS,
} from "@/constants/costGl";
import { buildFields } from "@/utils/costGl";

// scroll distance from the bottom (px) that triggers the next page
const LOAD_MORE_THRESHOLD = 80;
const SEARCH_DEBOUNCE_MS = 300;
// BE page-size ceiling, used when refreshing everything already loaded
const MAX_COST_GL_PAGE_SIZE = 5000;

const TAB_LABEL = {
  [MASTER_STATUS.PENDING]: "รอจัดการ",
  [MASTER_STATUS.READY]: "พร้อมใช้งาน",
  [MASTER_STATUS.CANCELLED]: "ถูกยกเลิก",
};

// One page for the three masters (treatment / lab / medicine & product):
// the route's meta.entity picks the configuration.
export default {
  name: "CostGlList",
  mixins: [tabQueryMixin],
  tabQuery: { keys: COST_GL_TABS, model: "activeTabIndex" },
  components: {
    Loading,
    Dialog,
    MasterStatusBadge,
    TxItem,
    DrugsItem,
    LabProductItem,
  },
  data() {
    return {
      MASTER_STATUS,
      activeTabIndex: 0,
      filter: "",
      rows: [],
      total: 0,
      isLoading: false,
      isLoadingMore: false,
      // bumped on every (re)load so a late response for an old tab / search is dropped
      requestId: 0,
      // search text of the last load; lets the debounced watcher skip a no-op reload
      loadedSearch: "",
    };
  },
  computed: {
    ...mapGetters({
      getCounts: "moduleCostGl/getCounts",
    }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    entity() {
      return COST_GL_ENTITIES[this.$route.meta.entity];
    },
    // ACCOUNTANT assigns the G/L
    canAssignGl() {
      return this.$can("cost_gl.manage");
    },
    // PROCUREMENT creates / edits / deletes the master (treatment, item); labs have no key
    canEditMaster() {
      return !!this.entity.writeKey && this.$can(this.entity.writeKey);
    },
    activeStatus() {
      return COST_GL_TABS[this.activeTabIndex] || MASTER_STATUS.PENDING;
    },
    counts() {
      return this.getCounts(this.entity.key);
    },
    tabs() {
      return COST_GL_TABS.map(status => ({
        status,
        label: TAB_LABEL[status],
        // like HQ approval: only รอจัดการ carries the count + red dot
        showCount: status === MASTER_STATUS.PENDING,
        showRedDot: status === MASTER_STATUS.PENDING,
        count: this.counts[status],
      }));
    },
    fields() {
      return buildFields(this.entity.key, this.activeStatus);
    },
    searchText() {
      return (this.filter || "").trim();
    },
    hasMore() {
      return this.rows.length < this.total;
    },
  },
  watch: {
    activeTabIndex() {
      this.filter = "";
      this.load();
    },
    // vue-router reuses this component between the three pages
    "$route.meta.entity"() {
      this.activeTabIndex = 0;
      this.filter = "";
      this.load();
    },
    // the list is paged, so searching has to be done by the server
    filter() {
      this.debouncedSearch();
    },
  },
  created() {
    this.debouncedSearch = debounce(this.onSearch, SEARCH_DEBOUNCE_MS);
  },
  beforeDestroy() {
    this.debouncedSearch.cancel();
  },
  async mounted() {
    await Promise.all([this.canAssignGl ? this.loadAccounts() : null, this.load()]);
  },
  methods: {
    ...mapActions({
      fetchAccounts: "moduleCostGl/fetchAccounts",
      fetchList: "moduleCostGl/fetchList",
      fetchFormOptions: "moduleClinicMaster/fetchFormOptions",
      removeMaster: "moduleClinicMaster/remove",
    }),
    // TxItem / DrugsItem report their own results through the page they sit in
    showDialogToast(type, message) {
      this.$refs.Dialog.showToast(type, message);
    },
    // Create (no item) or edit one master row in the shared treatment / item modal
    async openMasterModal(item) {
      let productList;
      try {
        // the G/L accounts are re-read on every open: roles may not have been loaded
        // when the page mounted, and accounts added since then must show up
        [productList] = await Promise.all([
          this.fetchFormOptions({
            kind: this.entity.key,
            clinicUrl: this.clinicUrl,
          }),
          this.canAssignGl ? this.loadAccounts() : null,
        ]);
      } catch (err) {
        this.$refs.Dialog.showToast("danger", this.errorText(err, "โหลดข้อมูลสำหรับฟอร์มไม่สำเร็จ"));
        console.error(err);
        return;
      }
      if (this.entity.key === "lab") {
        this.$refs.MasterModal.show(item);
        return;
      }
      const field = this.entity.key === "treat" ? "txItem" : "itemItem";
      this.$refs.MasterModal.show({ [field]: item, productList });
    },
    async deleteMaster(item) {
      const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
        this.entity.deleteTitle,
        `ต้องการลบ "${item.nameTh}" ออกจากทุกสาขา`
      );
      if (!isConfirmed) return;
      try {
        await this.removeMaster({
          kind: this.entity.key,
          clinicUrl: this.clinicUrl,
          id: item.id,
        });
        this.$refs.Dialog.showToast("success", `ลบรายการ "${item.nameTh}" สำเร็จ`);
      } catch (err) {
        this.$refs.Dialog.showToast("warning", this.errorText(err, `ลบรายการ "${item.nameTh}" ไม่สำเร็จ`));
        console.error(err);
      }
      await this.reload();
    },
    async loadAccounts() {
      try {
        await this.fetchAccounts({ clinicUrl: this.clinicUrl });
      } catch (err) {
        this.$refs.Dialog.showToast("danger", this.errorText(err, "โหลดรายการ Cost G/L ไม่สำเร็จ"));
        console.error(err);
      }
    },
    fetchPage({ offset, limit }) {
      return this.fetchList({
        clinicUrl: this.clinicUrl,
        entity: this.entity.key,
        status: this.activeStatus,
        search: this.searchText || undefined,
        offset,
        limit,
      });
    },
    // First page of the active tab (also used when the tab / page / search changes)
    async load() {
      const token = ++this.requestId;
      this.loadedSearch = this.searchText;
      this.isLoading = true;
      this.isLoadingMore = false;
      try {
        const res = await this.fetchPage({ offset: 0, limit: COST_GL_PAGE_SIZE });
        if (token !== this.requestId) return; // superseded by a newer load
        this.rows = res.data || [];
        this.total = res.count || 0;
      } catch (err) {
        if (token !== this.requestId) return;
        this.rows = [];
        this.total = 0;
        this.$refs.Dialog.showToast("danger", "โหลดข้อมูลไม่สำเร็จ");
        console.error(err);
      } finally {
        if (token === this.requestId) {
          this.isLoading = false;
          this.$nextTick(this.fillIfShort);
        }
      }
    },
    // Next page, appended below what is already shown
    async loadMore() {
      if (this.isLoading || this.isLoadingMore || !this.hasMore) return;
      const token = this.requestId;
      this.isLoadingMore = true;
      try {
        const res = await this.fetchPage({
          offset: this.rows.length,
          limit: COST_GL_PAGE_SIZE,
        });
        if (token !== this.requestId) return;
        const known = new Set(this.rows.map(r => r.id));
        this.rows = this.rows.concat(
          (res.data || []).filter(r => !known.has(r.id))
        );
        this.total = res.count || 0;
      } catch (err) {
        if (token !== this.requestId) return;
        this.$refs.Dialog.showToast("danger", "โหลดข้อมูลเพิ่มไม่สำเร็จ");
        console.error(err);
      } finally {
        if (token === this.requestId) {
          this.isLoadingMore = false;
          this.$nextTick(this.fillIfShort);
        }
      }
    },
    // After a save / cancel: refresh everything already loaded in one request,
    // so the user keeps their place instead of jumping back to the first page.
    async reload() {
      const token = ++this.requestId;
      this.isLoadingMore = false;
      const limit = Math.min(
        Math.max(this.rows.length, COST_GL_PAGE_SIZE),
        MAX_COST_GL_PAGE_SIZE
      );
      try {
        const res = await this.fetchPage({ offset: 0, limit });
        if (token !== this.requestId) return;
        this.rows = res.data || [];
        this.total = res.count || 0;
      } catch (err) {
        if (token !== this.requestId) return;
        this.$refs.Dialog.showToast("danger", "โหลดข้อมูลไม่สำเร็จ");
        console.error(err);
      }
    },
    onSearch() {
      if (this.searchText === this.loadedSearch) return;
      this.load();
    },
    onScroll() {
      const box = this.$refs.scrollBox;
      if (!box) return;
      if (box.scrollTop + box.clientHeight >= box.scrollHeight - LOAD_MORE_THRESHOLD) {
        this.loadMore();
      }
    },
    // A tall screen can show a whole page without a scrollbar, and then there is
    // nothing to scroll: keep loading until the box overflows or all rows are in.
    fillIfShort() {
      const box = this.$refs.scrollBox;
      if (box && box.clientHeight > 0 && box.scrollHeight <= box.clientHeight) {
        this.loadMore();
      }
    },
    // every master opens in its unified modal (G/L section inside)
    openForGl(item) {
      return this.openMasterModal(item);
    },
    errorText(err, fallback) {
      return (err && err.response && err.response.data && err.response.data.errorMessage) || fallback;
    },
  },
};
</script>

<style scoped>
.cost-gl-scroll {
  max-height: calc(100vh - 360px);
  min-height: 200px;
  overflow: auto;
}
/* Header stays put while the rows scroll. It needs its own opaque background
   (rows would show through) and a shadow instead of a border, because borders
   of a sticky cell do not travel with it under border-collapse.
   `!important` is required: bootstrap-vue puts `.position-relative`
   (`position: relative !important`) on every sortable header cell, which would
   otherwise beat sticky and let those columns scroll away with the rows. */
.cost-gl-scroll >>> thead th {
  position: sticky !important;
  top: 0;
  z-index: 2;
  background-color: #e9ecef;
  box-shadow: inset 0 -1px 0 #dee2e6;
}
.tab-red-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-left: 6px;
  border-radius: 50%;
  background: #dd3333;
  vertical-align: middle;
}
</style>
